import { createGamePkSelector } from './getGameStaticData';
import { createSelector } from 'reselect';
import { useMemo } from 'react';

const fallback = {
    disablePlayerLinks: true,
    headshotType: 'spots'
};

// This runs all the time, every selector call
// But the combiner will only run once per game based on the gamePk
const getLeagueContextDataHelper = (state) => {
    const {
        gumbo: {
            gameData: {
                teams: {
                    away = {},
                    home = {}
                } = {},
                game = {}
            } = {}
        } = {},
        boxscore: {
            teams
        } = {},
        config: {
            context = {}
        } = {}
    } = state || {};

    const awayPlayers = teams?.away?.playerById ?? {};
    const awayLeagueId = away?.league?.id ?? null;
    const homeLeagueId = home?.league?.id ?? null;

    const awayContext = context?.leaguesByAppContext ? context?.leaguesByAppContext[awayLeagueId] : null;
    const homeContext = context?.leaguesByAppContext ? context?.leaguesByAppContext[homeLeagueId] : null;

    return {
        gamePk: game.pk,
        awayPlayers,
        homeContext,
        awayContext,
        context
    };
};

export const getLeagueContextDataSelector = createGamePkSelector(
    getLeagueContextDataHelper,
    ({ awayPlayers, homeContext, awayContext, context }) => {
        // expensive thing ok since the selector cache should not invalidate
        const awayData = awayContext ? context?.configs[awayContext] : null;
        const homeData = homeContext ? context?.configs[homeContext] : null;

        return {
            awayPlayersMap: new Map(Object.entries(awayPlayers)),
            home: homeData,
            away: awayData,
            awayContext,
            homeContext
        };
    }
);

const getMatchedContextObj = (id, contextDataObj) => {
    // Some logic to figure out if the passed player is on a home or away team. Then assigning the correct contextDataObj.
    let matchedContextObj = {};

    if (!id) {
        return matchedContextObj;
    }

    if (contextDataObj.awayPlayersMap.has(id?.toString())) {
        const { away, awayContext } = contextDataObj;
        matchedContextObj = away;
        if (matchedContextObj && awayContext) {
            matchedContextObj = { ...away, playerPageContext: awayContext };
        }
    } else {
        const { home, homeContext } = contextDataObj;
        matchedContextObj = home;
        if (matchedContextObj && homeContext) {
            matchedContextObj = { ...home, playerPageContext: homeContext };
        }
    }

    return matchedContextObj;
};

export const getContextData = createSelector([
    getLeagueContextDataSelector
], (contextDataObj) => (id) => useMemo(() =>
    id ? getMatchedContextObj(id, contextDataObj) || fallback : fallback
, [id])
);
